import type { ReactNode } from 'react'
import { PageHeader } from './PageHeader'
import type { PageHeaderSearch } from './PageHeader'
import styles from './PageLayout.module.css'
import { TopBarActions } from './TopBarActions'
import { OnboardingPanel } from '@/components/onboarding/OnboardingPanel'
import { useOnboardingPageLock } from '@/hooks/useOnboardingLock'
import { usePeek } from '@/hooks/usePeek'
import { cn } from '@/lib/utils'

const peekClasses = {
  pinned: styles.pinned,
  unpinned: styles.unpinned,
  top: styles.top,
  notTop: styles.notTop,
}

/** What a `fullHeight` page's box needs to hand its own scroll to its children. */
const FULL_HEIGHT_BOX = 'min-h-0 min-w-0 flex flex-col'

type PageLayoutProps = {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  topActions?: ReactNode
  bottomActions?: ReactNode
  search?: PageHeaderSearch
  children: ReactNode
  contentClassName?: string
  fullHeight?: boolean
}

export function PageLayout({
  title,
  subtitle,
  actions,
  topActions,
  bottomActions,
  search,
  children,
  contentClassName,
  fullHeight = false,
}: PageLayoutProps) {
  const headerRef = usePeek({ classes: peekClasses })
  // The onboarding panel leads every page, and while the founder is still
  // locked the page it leads is dimmed and out of reach beneath it. Both are
  // no-ops for everyone else: the panel renders null without onboarding state,
  // and the lock is false.
  const contentLocked = useOnboardingPageLock()

  return (
    <div
      className={cn(
        'flex flex-col min-h-full min-w-0 w-full max-w-full',
        fullHeight && 'h-full min-h-0 flex-1',
      )}
    >
      <div
        ref={headerRef}
        data-testid="page-layout-header"
        className={cn(
          'sticky z-10 w-full max-w-full min-w-0 lg:fixed lg:left-[calc(6rem+271px)] lg:w-[calc(100vw-6rem-271px)]',
          styles.header,
        )}
      >
        <PageHeader
          title={title}
          subtitle={subtitle}
          actions={actions}
          topActions={
            <>
              <TopBarActions />
              {topActions}
            </>
          }
          bottomActions={bottomActions}
          search={search}
        />
      </div>
      <div className={styles.headerSpacer} />
      <div
        className={cn(
          // min-w-0 lets this flex item shrink below its content's intrinsic
          // width, so a wide child (e.g. a data table) scrolls inside its own
          // overflow container instead of widening the whole page sideways.
          'flex-1 min-w-0 p-4 lg:px-14 lg:py-10 bg-surface-muted',
          // Extra bottom padding on mobile so the last content (e.g. a form's
          // footer actions) can scroll clear of the floating Coach Grace FAB,
          // which sits fixed in the bottom-right corner on founder pages. Reset at
          // lg where the FAB no longer overlaps the wide content column. Skipped
          // for fullHeight pages, which manage their own internal scroll/layout.
          !fullHeight && 'pb-24 lg:pb-10',
          fullHeight && FULL_HEIGHT_BOX,
          contentClassName,
        )}
      >
        {/* First thing on the page, in its flow — see OnboardingPanel. A page
            that owns its own scroll gets the one-row form instead of the card. */}
        <OnboardingPanel compact={fullHeight} />
        {contentLocked ? (
          // `inert` (React 19) does what the old full-screen overlay's focus
          // trap did — no clicks, no tab stops, out of the accessibility tree —
          // without covering the panel that explains why.
          <div className={cn(styles.lockedContent, fullHeight && `${FULL_HEIGHT_BOX} flex-1`)} inert>
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  )
}
